import type { CruiseDayType } from "../types";
import { addDaysToKey, dateFromDayKey, dayKeyOf } from "./calendarDay";

/** Icon for a cruise day type — matches CaptainsLog. */
export function dayTypeIcon(dayType: CruiseDayType | null | undefined): string {
  switch (dayType) {
    case "embark": return "⚓";
    case "sea": return "🌊";
    case "port": return "🏝️";
    case "disembark": return "🏁";
    default: return "📅";
  }
}

/** Human label for a cruise day type. */
export function dayTypeLabel(dayType: CruiseDayType | null | undefined): string {
  switch (dayType) {
    case "embark": return "Embarkation Day";
    case "sea": return "Sea Day";
    case "port": return "Port Day";
    case "disembark": return "Disembarkation Day";
    default: return "Day";
  }
}

/** Default segment name for an itinerary day, e.g. "Day 3 — Sea Day" or
 *  "Day 4 — Nassau" when a port name is known. */
export function defaultDayName(
  dayNumber: number,
  dayType: CruiseDayType | null | undefined,
  portName?: string | null,
): string {
  const suffix = portName?.trim() ? portName.trim() : dayTypeLabel(dayType);
  return `Day ${dayNumber} — ${suffix}`;
}

export interface ItineraryDayDraft {
  dayNumber: number; // 1-based
  dayType: CruiseDayType;
  name: string;
  /**
   * Calendar date of the day, stored as noon UTC (see `utils/calendarDay`);
   * for an itinerary day startDate === endDate.
   */
  date: Date;
}

/**
 * Produce the day-by-day skeleton for a cruise: day 1 = embark, the last day =
 * disembark, everything in between = sea day. A cruise of `nights` nights has
 * `nights + 1` itinerary days.
 */
export function generateItineraryDays(startDate: Date, nights: number): ItineraryDayDraft[] {
  // Counted in calendar days from the sailing's first day, so neither the
  // phone doing the generating nor a DST change can slide a day.
  const firstDay = dayKeyOf(startDate) ?? "1970-01-01";
  const n = Math.max(0, Math.floor(nights));
  const totalDays = n + 1;
  const drafts: ItineraryDayDraft[] = [];
  for (let i = 0; i < totalDays; i++) {
    const dayNumber = i + 1;
    let dayType: CruiseDayType;
    if (dayNumber === 1) dayType = "embark";
    else if (dayNumber === totalDays) dayType = "disembark";
    else dayType = "sea";
    drafts.push({
      dayNumber,
      dayType,
      name: defaultDayName(dayNumber, dayType),
      date: dateFromDayKey(addDaysToKey(firstDay, i)),
    });
  }
  return drafts;
}

export interface ExistingItineraryDay {
  id: string;
  dayNumber: number | null | undefined;
  dayType: CruiseDayType | null | undefined;
  portName: string | null | undefined;
  notes: string | null | undefined;
  /** The day's current name, to tell a generated name from one the user typed. */
  name?: string | null;
  /** Whether this day has at least one activity (interaction) filed under it. */
  hasActivities: boolean;
}

/**
 * Day types the generator assigns by position. A day carrying one of these
 * wasn't typed by anyone — it was first or last when the itinerary was made —
 * so when the night count changes it moves with the position, rather than
 * leaving a second "Disembarkation Day" in the middle of the sailing.
 */
const POSITIONAL_DAY_TYPES: ReadonlySet<CruiseDayType> = new Set<CruiseDayType>(["sea", "embark", "disembark"]);

function isPositionalType(dayType: CruiseDayType | null | undefined): boolean {
  return !dayType || POSITIONAL_DAY_TYPES.has(dayType);
}

/**
 * A day is "customized" — and therefore never removed by Regenerate — if the
 * user has touched it: made it a port day (or any type the generator doesn't
 * assign), named a port, written day notes, or logged activities under it.
 */
export function isDayCustomized(day: ExistingItineraryDay): boolean {
  if (!isPositionalType(day.dayType)) return true;
  if (day.portName?.trim()) return true;
  if (day.notes && day.notes.trim() && day.notes.trim() !== "<p><br></p>") return true;
  if (day.hasActivities) return true;
  return false;
}

export interface DayPatch {
  name?: string;
  dayType?: CruiseDayType;
  startDate?: Date;
  endDate?: Date;
}

export interface ItineraryReconciliation {
  toCreate: ItineraryDayDraft[];
  toUpdate: { id: string; patch: DayPatch }[];
  toRemove: { id: string; dayNumber: number | null | undefined }[];
}

/**
 * Diff the freshly generated `drafts` against the days that already exist.
 * Existing days keep their identity (and their activities); customized days are
 * never altered or removed; uncustomized days have their calendar date / name /
 * type refreshed; surplus uncustomized trailing days are flagged for removal so
 * the caller can confirm before deleting them.
 */
export function reconcileItinerary(
  existingDays: ExistingItineraryDay[],
  drafts: ItineraryDayDraft[],
): ItineraryReconciliation {
  const byDayNumber = new Map<number, ExistingItineraryDay>();
  for (const day of existingDays) {
    if (typeof day.dayNumber === "number") byDayNumber.set(day.dayNumber, day);
  }

  const toCreate: ItineraryDayDraft[] = [];
  const toUpdate: { id: string; patch: DayPatch }[] = [];

  for (const draft of drafts) {
    const existing = byDayNumber.get(draft.dayNumber);
    if (!existing) {
      toCreate.push(draft);
      continue;
    }
    const patch: DayPatch = { startDate: draft.date, endDate: draft.date };
    if (!isDayCustomized(existing)) {
      patch.name = draft.name;
      patch.dayType = draft.dayType;
    } else if (isPositionalType(existing.dayType) && existing.dayType !== draft.dayType) {
      // Kept for its port, notes or activities — but its embark/sea/disembark
      // type came from where it sat, so it follows the new position. Only a
      // name the generator wrote is refreshed with it.
      patch.dayType = draft.dayType;
      const generatedName = defaultDayName(draft.dayNumber, existing.dayType, existing.portName);
      if (existing.name == null || existing.name === generatedName) {
        patch.name = defaultDayName(draft.dayNumber, draft.dayType, existing.portName);
      }
    }
    toUpdate.push({ id: existing.id, patch });
  }

  const draftNumbers = new Set(drafts.map((d) => d.dayNumber));
  const toRemove: { id: string; dayNumber: number | null | undefined }[] = [];
  for (const day of existingDays) {
    const dn = typeof day.dayNumber === "number" ? day.dayNumber : null;
    if (dn !== null && draftNumbers.has(dn)) continue;
    if (isDayCustomized(day)) continue;
    toRemove.push({ id: day.id, dayNumber: dn });
  }

  return { toCreate, toUpdate, toRemove };
}
